import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { VariantQtyRows } from './VariantQtyRows';
import { ShippingAddressForm } from './ShippingAddressForm';
import { PaymentStatusToggle } from './PaymentStatusToggle';
import { Thumbnail } from './Thumbnail';
import { get, post, ApiError } from '@/lib/api';
import { useSession } from '@/lib/auth';
import {
  type AnySub, type ShippingFields, type ShopifyVariant, type OrderLine,
  latestLogoPath, prefillShipping, shippingComplete, buildPlaceOrderPayload, isReorder,
  placeOrderErrorMessage,
} from '@/lib/ordering';
import { mintedShopifyProducts, type MintedShopifyProduct } from '@/lib/prize-tiers';

type VariantsPayload = {
  variants?: ShopifyVariant[];
  product?: { title?: string; image?: string; artwork_url?: string };
  error?: string;
};

function ProductOrderCard({
  item,
  productName,
  logoPath,
  qty,
  onQty,
}: {
  item: MintedShopifyProduct;
  productName: string;
  logoPath: string;
  qty: Record<string, number>;
  onQty: (id: string, n: number) => void;
}) {
  const { data, isLoading } = useSWR<VariantsPayload>(
    item.shopifyProductId ? `/api/shopify-product?productId=${encodeURIComponent(item.shopifyProductId)}` : null,
    get,
  );
  const variants = data?.variants || [];
  const image = data?.product?.image || item.mockupUrl || undefined;
  const artworkUrl = data?.product?.artwork_url;
  const subtotal = Object.values(qty).reduce((s, n) => s + n, 0);
  const tone = item.index === 1 ? 'bg-primary' : item.index === 2 ? 'bg-[#8b5cf6]' : 'bg-text';
  return (
    <div className="rounded-md border border-border bg-bg/40 p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold text-white ${tone}`}>P{item.index}</span>
        <div className="min-w-0">
          <div className="truncate text-[12.5px] font-semibold">{productName}</div>
          <div className="truncate font-mono text-[10px] text-subtle">{item.product || 'No SKU'}</div>
        </div>
      </div>
      <div className="mb-2 grid grid-cols-2 gap-2">
        <div className="rounded-sm border border-border bg-surface p-1.5">
          <div className="mb-1 text-[9.5px] font-semibold uppercase tracking-wide text-subtle">Approved logo</div>
          {logoPath ? (
            <Thumbnail path={logoPath} alt={`Product ${item.index} logo`} className="h-24 w-full" />
          ) : artworkUrl ? (
            <img src={artworkUrl} alt={`Product ${item.index} logo`} className="h-24 w-full rounded-sm bg-bg object-contain" />
          ) : (
            <div className="flex h-24 items-center justify-center rounded-sm bg-bg text-[10px] text-subtle">No logo</div>
          )}
        </div>
        <div className="rounded-sm border border-border bg-surface p-1.5">
          <div className="mb-1 text-[9.5px] font-semibold uppercase tracking-wide text-subtle">Product</div>
          {image ? (
            <img src={image} alt={`Product ${item.index} product`} className="h-24 w-full rounded-sm bg-bg object-contain" />
          ) : (
            <div className="flex h-24 items-center justify-center rounded-sm bg-bg text-[10px] text-subtle">No image</div>
          )}
        </div>
      </div>
      {isLoading ? (
        <div className="space-y-1.5 p-2">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
        </div>
      ) : data?.error ? (
        <div className="p-3 text-center text-[11.5px] text-danger">{String(data.error)}</div>
      ) : (
        <VariantQtyRows variants={variants} qty={qty} onChange={onQty} />
      )}
      <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-[11.5px]">
        <span className="text-muted">Product {item.index} subtotal</span>
        <span className="font-semibold">{subtotal} units</span>
      </div>
    </div>
  );
}

function CollectLines({
  item,
  qty,
  onLines,
}: {
  item: MintedShopifyProduct;
  qty: Record<string, number>;
  onLines: (index: number, lines: OrderLine[]) => void;
}) {
  const { data } = useSWR<VariantsPayload>(
    item.shopifyProductId ? `/api/shopify-product?productId=${encodeURIComponent(item.shopifyProductId)}` : null,
    get,
  );
  const lines = useMemo<OrderLine[]>(() => (
    (data?.variants || [])
      .map((v) => ({
        variant_id: v.id,
        product_id: item.shopifyProductId,
        sku: v.sku,
        quantity: qty[String(v.id)] || 0,
      }))
      .filter((l) => l.quantity > 0)
  ), [data, qty, item.shopifyProductId]);
  useEffect(() => { onLines(item.index, lines); }, [item.index, lines, onLines]);
  return null;
}

// Place Order modal — loops minted Shopify ids (tiers[].shopifyProductId or
// shopifyProductIds[]). Empty T2 is omitted. metadata.products matches
// create-order after #280.
export function PlaceOrderDialog({
  sub,
  open,
  onClose,
  onPlaced,
  productNames,
}: {
  sub: AnySub | null;
  open: boolean;
  onClose: () => void;
  onPlaced: () => void;
  productNames: (id?: string) => string;
}) {
  const { user } = useSession();
  const [qtyByIndex, setQtyByIndex] = useState<Record<number, Record<string, number>>>({});
  const [linesByIndex, setLinesByIndex] = useState<Record<number, OrderLine[]>>({});
  const [shipping, setShipping] = useState<ShippingFields>(() => prefillShipping(sub || {}));
  const [email, setEmail] = useState('');
  const [financialStatus, setFinancialStatus] = useState<'paid' | 'pending'>('paid');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const minted = useMemo(() => (sub && open ? mintedShopifyProducts(sub) : []), [sub, open]);

  useEffect(() => {
    if (open && sub) {
      setQtyByIndex({});
      setLinesByIndex({});
      setShipping(prefillShipping(sub));
      setEmail(sub.email || sub.shippingAddress?.email || sub.shipping?.email || '');
      setFinancialStatus('paid');
      setNote('');
    }
  }, [open, sub?.id]);

  const onLines = useMemo(() => (
    (index: number, lines: OrderLine[]) => {
      setLinesByIndex((prev) => {
        const prevLines = prev[index] || [];
        if (prevLines.length === lines.length && prevLines.every((l, i) => l.variant_id === lines[i].variant_id && l.quantity === lines[i].quantity)) {
          return prev;
        }
        return { ...prev, [index]: lines };
      });
    }
  ), []);

  const lines = minted.flatMap((p) => linesByIndex[p.index] || []);
  const totalUnits = lines.reduce((s, l) => s + l.quantity, 0);

  if (!sub) return null;
  const reorder = isReorder(sub);

  const submit = async () => {
    if (!lines.length) { toast.error('Enter at least one item quantity'); return; }
    if (!shippingComplete(shipping)) { toast.error('Fill in all required shipping fields'); return; }
    const name = sub.fundraiserName || sub.id;
    if (!confirm(
      `Create a REAL $0 Shopify order for ${totalUnits} units for "${name}"?` +
      (reorder ? '\n\nThis is an ADDITIONAL order for a fundraiser that has already been ordered.' : '')
    )) return;

    setBusy(true);
    try {
      const payload = buildPlaceOrderPayload({
        sub, lines, shipping, email, financialStatus, note,
        orderedBy: user?.email || 'console',
      }) as { order: unknown; metadata: Record<string, unknown> };
      type PlaceResult = { success?: boolean; orderNumber?: string; orderId?: number; order?: { name?: string } };
      // create-order refuses past blank stock (409/503, nothing created); the
      // outer catch puts its reason in front of the operator.
      const r = await post<PlaceResult>('/api/orders/place', payload);
      toast.success(`Shopify order ${r.orderNumber || r.order?.name || r.orderId || ''} created (${financialStatus === 'paid' ? 'marked as Paid' : 'payment pending'})`);
      onPlaced();
      onClose();
    } catch (e) {
      // Stock refusal, unfinished approval (APPROVAL_INCOMPLETE) and the
      // duplicate-order guard all answer 409 — placeOrderErrorMessage tells
      // them apart by body shape. pre-line keeps its error / detail / hint
      // paragraphs apart instead of collapsing them into one run-on line.
      toast.error(e instanceof ApiError
        ? placeOrderErrorMessage(e.status, e.body, e.message)
        : placeOrderErrorMessage(null, null, (e as Error).message || undefined),
      { style: { whiteSpace: 'pre-line' } });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[88vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{sub.fundraiserName || 'Unnamed fundraiser'}</DialogTitle>
          <DialogDescription>
            Admin: {sub.adminName || '-'} · {sub.id}
          </DialogDescription>
        </DialogHeader>

        {reorder && (
          <div className="rounded-md border border-warning/60 bg-warning/10 px-3 py-2 text-[12px] text-warning">
            <strong className="mb-0.5 block">Reorder notice</strong>
            This team has already received <strong>{Number(sub.orderCount) || 1}</strong> order{(Number(sub.orderCount) || 1) === 1 ? '' : 's'}
            {typeof sub.lastOrderNumber === 'string' && sub.lastOrderNumber ? <> — most recent <strong>{sub.lastOrderNumber.replace(/[<>"'&]/g, '')}</strong></> : null}.
            Placing this order will create an <em>additional</em> Shopify order.
          </div>
        )}

        <div className="grid gap-3 lg:grid-cols-[1.5fr_1fr]">
          <div className="space-y-3">
            {minted.length === 0 && (
              <div className="rounded-md border border-border bg-bg/40 p-3 text-[12px] text-muted">
                No minted Shopify products on this submission yet.
              </div>
            )}
            {minted.map((item) => (
              <div key={item.index}>
                <CollectLines item={item} qty={qtyByIndex[item.index] || {}} onLines={onLines} />
                <ProductOrderCard
                  item={item}
                  productName={productNames(item.product || undefined)}
                  logoPath={latestLogoPath(sub, item.index)}
                  qty={qtyByIndex[item.index] || {}}
                  onQty={(id, n) => setQtyByIndex((q) => ({ ...q, [item.index]: { ...(q[item.index] || {}), [id]: n } }))}
                />
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <div className="rounded-md border border-border bg-bg/40 p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-subtle">Customer & shipping</div>
              <ShippingAddressForm value={shipping} onChange={setShipping} email={email} onEmailChange={setEmail} />
            </div>
            <div className="rounded-md border border-border bg-bg/40 p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-subtle">Payment status</div>
              <PaymentStatusToggle value={financialStatus} onChange={setFinancialStatus} />
            </div>
            <div className="rounded-md border border-border bg-bg/40 p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-subtle">Order notes</div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Special instructions for this order…"
                className="h-16 w-full resize-y rounded-sm border border-border bg-bg px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="rounded-md border border-border bg-bg/40 p-3 text-[12px]">
              {minted.map((item) => (
                <div key={item.index} className="flex justify-between">
                  <span className="text-muted">Product {item.index} units</span>
                  <span>{Object.values(qtyByIndex[item.index] || {}).reduce((s, n) => s + n, 0)}</span>
                </div>
              ))}
              <div className="mt-1 flex justify-between border-t border-border pt-1 font-semibold"><span>Total units</span><span>{totalUnits}</span></div>
            </div>
            <Button className="w-full" size="lg" disabled={busy || totalUnits === 0} onClick={submit}>
              {busy ? 'Creating order…' : reorder ? 'Place Additional Order' : 'Create Shopify Order'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
